import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Chip,
  useTheme,
  IconButton,
  Fab,
} from '@mui/material';
import {
  Refresh,
  Add,
  CheckCircle,
  Schedule,
  Lock,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { UserContext } from '../../../components/shared/useUser';
import {
  getCashTransactionClient,
  TillStateDto,
  formatCashAmount,
  getReconciliationStatusColor,
} from '../../../app/services/api/cashTransactions';
import useLogError from '../../../hooks/useLogError';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';

/**
 * Mobile-optimized till dashboard
 * Large cards for one-handed operation
 */
const MobileTillDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();

  const [loading, setLoading] = useState(false);
  const [tillStates, setTillStates] = useState<TillStateDto[]>([]);

  const cashClient = getCashTransactionClient();

  const hasWritePermission = hasPermissionTo(['CashReconciliationWrite']);

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      loadTills();
    }
  }, [selectedStore?.storeNumber]);

  const loadTills = async () => {
    if (!selectedStore?.storeNumber) return;

    setLoading(true);
    try {
      const tillsData = await cashClient.getAllTillStates(selectedStore.storeNumber);
      setTillStates(tillsData);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTillClick = (till: TillStateDto) => {
    if (till.status === 'Open') {
      navigate(`/cash-reconciliation/mobile/till/${till.tillNumber}`);
    } else {
      // Navigate to till history or reopen flow
      navigate(`/cash-reconciliation/mobile/till/${till.tillNumber}/history`);
    }
  };

  const handleOpenTill = () => {
    navigate('/cash-reconciliation/mobile/open-till');
  };

  const getTillStatusIcon = (status: string) => {
    switch (status) {
      case 'Open':
        return <CheckCircle sx={{ color: theme.palette.success.main }} />;
      case 'Closed':
        return <Lock sx={{ color: theme.palette.custom.gray[500] }} />;
      default:
        return <Schedule sx={{ color: theme.palette.warning.main }} />;
    }
  };

  const getTimeAgo = (timestamp?: string) => {
    if (!timestamp) return '';
    const diff = dayjs().diff(dayjs(timestamp), 'hour');
    if (diff < 1) return 'Just now';
    if (diff < 24) return `${diff}h ago`;
    return dayjs(timestamp).format('DD/MM HH:mm');
  };

  return (
    <PageArea>
      <Box sx={{ pb: 10 }}> {/* Bottom padding for FAB */}
        {/* Header */}
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}>
          <PageTitle subTitle="Tap a till to manage">My Tills</PageTitle>
          <IconButton onClick={loadTills}
            size="large">
            <Refresh />
          </IconButton>
        </Stack>

        {/* Till Grid - Large Cards */}
        <Grid container
          spacing={2}>
          {tillStates.map((till) => (
            <Grid item
              xs={12}
              sm={6}
              key={till.tillNumber}>
              <Card
                sx={{
                  minHeight: '180px',
                  border: till.status === 'Open'
                    ? `3px solid ${theme.palette.success.main}`
                    : `1px solid ${theme.palette.divider}`,
                  bgcolor: till.status === 'Open'
                    ? theme.palette.success.light + '10'
                    : 'background.paper',
                }}
                elevation={till.status === 'Open' ? 4 : 1}
              >
                <CardActionArea
                  onClick={() => handleTillClick(till)}
                  sx={{ height: '100%', p: 2 }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Stack spacing={2}>
                      {/* Header Row */}
                      <Stack direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Typography variant="displaySM"
                          fontWeight={700}>
                          {till.tillNumber}
                        </Typography>
                        {getTillStatusIcon(till.status)}
                      </Stack>

                      {/* Status Chip */}
                      <Chip
                        label={till.status}
                        size="medium"
                        color={
                          getReconciliationStatusColor(till.status) as
                          | 'default'
                          | 'primary'
                          | 'secondary'
                          | 'error'
                          | 'info'
                          | 'success'
                          | 'warning'
                        }
                        sx={{ width: 'fit-content' }}
                      />

                      {/* Balance */}
                      <Typography variant="displayMD"
                        fontWeight={700}
                        color="primary">
                        {formatCashAmount(till.currentBalance, 'GBP')}
                      </Typography>

                      {/* User and Time */}
                      <Stack direction="row"
                        spacing={1}
                        alignItems="center">
                        {till.assignedUsername && (
                          <>
                            <Person fontSize="small"
                              sx={{ color: 'text.secondary' }} />
                            <Typography variant="textSM"
                              color="text.secondary">
                              {till.assignedUsername}
                            </Typography>
                          </>
                        )}
                      </Stack>

                      {till.lastReconciliationTimestamp && (
                        <Typography variant="textXS"
                          color="text.secondary">
                          Last count: {getTimeAgo(till.lastReconciliationTimestamp)}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}

          {tillStates.length === 0 && !loading && (
            <Grid item
              xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}
                    alignItems="center"
                    py={4}>
                    <Lock sx={{ fontSize: 64, color: 'text.secondary' }} />
                    <Typography variant="textMD"
                      color="text.secondary">
                      No tills configured
                    </Typography>
                    <Typography variant="textSM"
                      color="text.secondary"
                      textAlign="center">
                      Contact your manager to set up cash zones
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Floating Action Button */}
      {hasWritePermission && (
        <Fab
          color="primary"
          aria-label="open till"
          onClick={handleOpenTill}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 72,
            height: 72,
          }}
        >
          <Add sx={{ fontSize: 32 }} />
        </Fab>
      )}
    </PageArea>
  );
};

export default MobileTillDashboard;
